import { ChangeEvent, useCallback, useEffect, useMemo, useState } from "react";
import { useQuery } from "react-query";
import { useAppSelector } from "redux/hooks";
import { ProfileAccountInfo } from "api/interfaces/accountInterface.interface";
import {
  CovaPermission,
  RecorderTypePermissionWithoutRole,
  SpectrumPermission,
  StrictUserPermissionKey,
  UserGroupUserPermission,
  VmaxPermission,
  UserGroupCURequest,
  UserGroupCUSystem,
  UserGroupCUSystemCamera,
} from "api/interfaces/userInterface.interface";
import { notify } from "components/atoms/notification/Notification";
import {
  createGroup,
  deleteGroup,
  getUserGroupInfo,
  updateGroup,
} from "api/uGroupAPI";
import { checkWhiteSpaceRegEx } from "utils/regEx";
import {
  CustomError,
  LEVEL_TYPE,
  Validate,
  ValidateMsg,
} from "api/interfaces/commonInterface.interface";
import { useAuth } from "components/Auth";
import useApiError from "hook/useApiError";
import {
  RecorderGroupListResponse,
  RecorderGroupSystem,
  RecorderTotalListForUserGroupSystem,
} from "api/interfaces/recorderInterface.interface";
import {
  getRecorderGroupTotalList,
  getRecorderTotalListForUserGroup,
} from "api/recorderAPI";
import {
  allDisabledUserPermission,
  defaultGroupName,
  defaultCheckedUserPermission,
  checkAutoApply,
  allDisabledRecorderPermission,
  customDisabledRecorderPermission,
  RecorderType,
  SpectrumRoleLevel,
  NVRRoleLevel,
  allFormatLevel,
  formatLevelCheckList,
  currentRecorderPermissionDisableList,
} from "./PermissionPolicy";
import useStatusMutation from "hook/useStatusMutation";
import { cloneDeep } from "lodash";
import { useDwPermission } from "hook/useDwPermission";
import AddUserGroupPresenter from "./AddUserGroupPresenter";
import { checkTypeIsCovaOrVmax } from "utils/RecorderUtil";
import { useMenu } from "hook/useMenu";
import useStompDebouncedRefetch, {
  StompDateSignalKey,
} from "hook/useStompDebouncedRefetch";
import { useQueryClient } from "react-query";
import { checkIsSubCP } from "utils/AccountUtil";

export interface AddUserGroupProps {
  onModal: (type?: string) => void;
  onSuccessDelete?: () => void;
  groupId?: string;
}

export enum UPDATE_TYPE {
  USERS,
  USER_PERMISSION,
  ACCOUNT,
  AUTO_APPLY,
  AUTO_APPLY_ACCOUNT,
  RECORDERS,
  RECORDER_CAMEARS,
  RECORDER_GROUPS,
  RECORDER_LEVEL,
  RECORDER_PERMISSION,
  NAME,
  DESC,
}

interface RecorderGroupList extends RecorderGroupSystem {
  recorderGroupId: number;
}

const filteringSystemByCameraSelected = (
  systems?: UserGroupCUSystem[]
): UserGroupCUSystem[] => {
  return (systems ?? []).filter((system) => {
    if (checkTypeIsCovaOrVmax(system)) {
      return (
        (system.cameras && system.cameras.some((cam) => cam.isAccess)) ||
        system.allCameras
      );
    }
    return true;
  });
};

const areAllCamerasSelected = (
  cameras?: UserGroupCUSystemCamera[]
): boolean => {
  return (
    (cameras?.length ?? 0) > 0 && cameras!.every((camera) => camera.isAccess)
  );
};

const normalizeSystemAllCameras = (
  system: UserGroupCUSystem
): UserGroupCUSystem => {
  if (!checkTypeIsCovaOrVmax(system)) {
    return { ...system, allCameras: true };
  }

  return {
    ...system,
    allCameras: areAllCamerasSelected(system.cameras),
  };
};

const syncRecorderGroupsBySelectedSystems = ({
  recorderGroups,
  previousSystems,
  nextSystems,
  recorderGroupList,
}: {
  recorderGroups: number[];
  previousSystems?: UserGroupCUSystem[];
  nextSystems: UserGroupCUSystem[];
  recorderGroupList?: RecorderGroupListResponse["result"];
}): number[] => {
  const prevSelectedSystemIds = new Set(
    (previousSystems ?? []).map((system) => system.mergedSystemId)
  );
  const nextSelectedSystemIds = new Set(
    nextSystems.map((system) => system.mergedSystemId)
  );

  const deselectedSystemIds = Array.from(prevSelectedSystemIds).filter(
    (systemId) => !nextSelectedSystemIds.has(systemId)
  );

  if (deselectedSystemIds.length === 0) {
    return recorderGroups;
  }

  const deselectedSystemIdSet = new Set(deselectedSystemIds);

  return recorderGroups.filter((recorderGroupId) => {
    const recorderGroup = recorderGroupList?.find(
      (group) => group.recorderGroupId === recorderGroupId
    );

    return !(recorderGroup?.systems ?? []).some((system) =>
      deselectedSystemIdSet.has(system.mergedSystemId)
    );
  });
};

const applyAllCamerasByRecorderRole = ({
  systems,
  recorderPermission,
}: {
  systems?: UserGroupCUSystem[];
  recorderPermission: UserGroupCURequest["recorderPermission"];
}): UserGroupCUSystem[] | undefined => {
  if (!systems) return systems;

  const isCovaAdmin = recorderPermission.cova.role === 10;
  const isVmaxAdmin = recorderPermission.vmax.role === 10;

  return systems.map((system) => {
    if (!checkTypeIsCovaOrVmax(system)) return system;

    const normalizedDisplayType = (system.displayType ?? "").toUpperCase();
    const isCova = normalizedDisplayType === "COVA";
    const isAdmin = isCova ? isCovaAdmin : isVmaxAdmin;

    if (!isAdmin) return { ...system };

    return {
      ...system,
      allCameras: true,
      cameras: system.cameras?.map((camera) => ({ ...camera, isAccess: true })),
    };
  });
};

function AddUserGroupContainer(props: AddUserGroupProps): JSX.Element {
  const [page, setPage] = useState<number>(0);
  const selectedAccount: ProfileAccountInfo = useAppSelector(
    (state) => state.accountSettings
  );

  const { accountLevelExtendSubCp } = selectedAccount;

  const queryClient = useQueryClient();

  const {
    accountLevelPermissionNames,
    invertPermissionBooleans,
    defaultDisabledUserPermission,
    normalizeUserGroupUserPermission,
    isAllCheckForEnableAllPermission,
  } = useDwPermission({});

  const isAdminGroup = selectedAccount.isAdminGroup;
  const defaultPermissions = defaultCheckedUserPermission(
    selectedAccount.isAdminGroup
  )[accountLevelExtendSubCp];

  const initialUserGroupData: UserGroupCURequest = {
    userPermission: normalizeUserGroupUserPermission({
      ...invertPermissionBooleans(
        defaultCheckedUserPermission(true)[accountLevelExtendSubCp]
      ),
    }),
    recorderPermission: {
      ...cloneDeep(allFormatLevel({ checkType: "check" })),
    },
    name: "",
    description: "",
    ...checkAutoApply({
      selectedAccount,
    }),
  };

  const { handleErrorNotify, intlString, lang } = useApiError();
  const auth = useAuth();

  const [userGroupValidate, setUserGroupValidate] = useState<Validate>({
    isUserGroupName: false,
    isUserPermission: false,
    isUsers: false,
    isRecorder: false,
  });
  const { updateCurrentAccount } = useMenu({});
  const [userGroupValidateMsg, setUserGroupValidateMsg] = useState<ValidateMsg>(
    {
      isUserGroupName: "",
      isUserPermission: "",
      isUsers: "",
      isRecorder: "",
    }
  );

  const [userGroup, setUserGroup] = useState<UserGroupCURequest>({
    ...initialUserGroupData,
  });

  const [myDwDisabledList, setMyDwDisabledList] =
    useState<UserGroupUserPermission>({
      ...defaultDisabledUserPermission,
    });
  const [recorderRolepermissionDisabled, setRecorderRolePermissionDisabled] =
    useState<boolean>(false);

  const [recorderPermissionDisabledList, setRecorderPermissionDisableList] =
    useState<RecorderTypePermissionWithoutRole>({
      ...cloneDeep(customDisabledRecorderPermission),
    });

  const [isEditableUser, setIsEditableUser] = useState<boolean>(true);

  const [selectedRecorderTypes, setSelectedRecorderTypes] = useState<string[]>(
    []
  );

  const { data: recorderResult, refetch: refetchRecorder } = useQuery(
    ["recorder", selectedAccount.accountId, auth.user.userId],
    () =>
      getRecorderTotalListForUserGroup({
        accountId: selectedAccount.accountId,
        userId: auth.user.userId,
      }),
    {
      retry: 0,
      refetchOnWindowFocus: false,
      onError: (e: any) => {},
    }
  );

  const { data: recorderGroupResult, refetch: refetchRecorderGroup } = useQuery(
    ["recorderGroup", selectedAccount.accountId, auth.user.userId],
    () =>
      getRecorderGroupTotalList({
        accountId: selectedAccount.accountId,
        userId: auth.user.userId,
      }),
    {
      retry: 0,
      refetchOnWindowFocus: false,
      onError: (e: any) => {},
    }
  );
  const refetch = useCallback(() => {
    refetchRecorder();
    refetchRecorderGroup();
  }, [refetchRecorder, refetchRecorderGroup]);
  useStompDebouncedRefetch({
    stompKey: ["recorderListUpdated"] as StompDateSignalKey[],
    refetch: refetch,
  });

  const recorderSystems = useMemo<RecorderTotalListForUserGroupSystem[]>(() => {
    if (recorderResult?.result && recorderResult.error === 0) {
      return recorderResult.result;
    }

    return [];
  }, [recorderResult]);

  const recorderGroups = useMemo<RecorderGroupList[]>(() => {
    if (recorderGroupResult?.result && recorderGroupResult.error === 0) {
      return recorderGroupResult.result.flatMap(
        (group: RecorderGroupListResponse["result"][number]) =>
          group.systems?.map((system: RecorderGroupSystem) => ({
            ...system,
            recorderGroupId: group.recorderGroupId,
          })) || []
      );
    }

    return [];
  }, [recorderGroupResult]);

  const buildRecorderSystemPayload = useCallback(
    (mergedSystemId: string): UserGroupCUSystem | null => {
      const recorderSystem = recorderSystems.find(
        (system) => system.mergedSystemId === mergedSystemId
      );

      if (!recorderSystem) return null;

      const isNoSpectrum =
        checkTypeIsCovaOrVmax(recorderSystem) &&
        (recorderSystem.cameras?.length ?? 0) > 0;

      if (!isNoSpectrum) {
        return normalizeSystemAllCameras({
          ...recorderSystem,
        });
      }

      return normalizeSystemAllCameras({
        ...recorderSystem,
        cameras: (recorderSystem.cameras ?? [])
          .filter((camera) => !camera.isDisabled)
          .map((camera) => ({
            channel: camera.channel,
            cameraId: camera.cameraId,
            isAccess: true,
            cameraUuid: camera.cameraUuid,
          })),
      });
    },
    [recorderSystems]
  );

  const checkNoRecorder = useMemo(() => {
    const requireRecorderGroups = recorderGroups.length > 0;
    const requireSystems = recorderSystems.length > 0;

    const hasRecorderGroups = (userGroup.recorderGroups?.length ?? 0) > 0;
    const hasSystems = (userGroup.systems?.length ?? 0) > 0;

    /**
     * recorder Group 선택하면 속한 Recorder 들 선택되는 로직으로 변경
     * -> recorderGrup 은 비교할 필요 없으므로 주석처리
     */
    // 조건: recorderList 에 뭔가 있는데 userGroup 이 전혀 선택 안 했을 때만 true
    const noSelection =
      // requireRecorderGroups ||
      requireSystems &&
      // !hasRecorderGroups &&
      !hasSystems;

    return (
      accountLevelExtendSubCp === LEVEL_TYPE.EU &&
      !userGroup.autoApplyRecorder &&
      noSelection
    );
  }, [
    accountLevelExtendSubCp,
    userGroup.autoApplyRecorder,
    // recorderGroups,
    recorderSystems,
    // userGroup.recorderGroups,
    userGroup.systems,
  ]);

  const checkDwPermissionIsFalse = useMemo(() => {
    // 현재 dw permission 이 체크해제된 것만 key값으로 추출한 배열
    const isCheckAllPermissionIsFalse = accountLevelPermissionNames.filter(
      (key) =>
        userGroup.userPermission[key as StrictUserPermissionKey] === false
    );
    // dwpermission 체크 개수가 permission false 상태 배열 개수가 같다면 return

    return (
      isCheckAllPermissionIsFalse.length === accountLevelPermissionNames.length
    );
  }, [accountLevelPermissionNames, userGroup.userPermission]);

  const onValidateUserPermission = useCallback(() => {
    setUserGroupValidate((info) => {
      return { ...info, isUserPermission: true as boolean };
    });

    setUserGroupValidateMsg((info) => {
      return {
        ...info,
        isUserPermission: intlString(lang.validateMsg.userGroup.userPermission),
      };
    });
  }, [setUserGroupValidate, setUserGroupValidateMsg]);

  const onValidateRecorder = useCallback(() => {
    setUserGroupValidate((info) => {
      return { ...info, isRecorder: true as boolean };
    });

    setUserGroupValidateMsg((info) => {
      return {
        ...info,
        isRecorder: intlString(lang.validateMsg.userGroup.recorder),
      };
    });
  }, [setUserGroupValidate, setUserGroupValidateMsg]);

  const { data: userGroupInfoResult } = useQuery(
    ["userGroupInfo", selectedAccount.accountId, props.groupId],
    () =>
      getUserGroupInfo({
        accountId: selectedAccount.accountId,
        groupId: props.groupId ?? "",
      }),
    {
      retry: 0,
      refetchOnWindowFocus: false,
      enabled: !!props.groupId,
    }
  );

  useEffect(() => {
    // user 권한이 admin이 아니면 role custom disabled
    if (accountLevelExtendSubCp === LEVEL_TYPE.EU) {
      setRecorderRolePermissionDisabled(!isAdminGroup);
    }
    if (userGroupInfoResult?.result && userGroupInfoResult.error === 0) {
      const tempUserGroupInfo = userGroupInfoResult.result;
      const enableEditRule = tempUserGroupInfo.enableDelete; //default rule 이 아닐 때를 의미
      const isHostUser = tempUserGroupInfo.hostEmail === auth.user.email; // usergroup 만든 host

      if (enableEditRule && (isAdminGroup || isHostUser)) {
        // --- Case 1: default Rule 아님 + 관리자 or 호스트 ---
        setIsEditableUser(true);
      } else {
        setIsEditableUser(false);
        setMyDwDisabledList(allDisabledUserPermission);

        setRecorderPermissionDisableList(
          cloneDeep(allDisabledRecorderPermission)
        );

        if (!enableEditRule) {
          setRecorderRolePermissionDisabled(true);
        }
      }

      setUserGroup({
        ...checkAutoApply({
          selectedAccount,
        }),
        ...(tempUserGroupInfo as UserGroupCURequest),
        recorderPermission: {
          ...tempUserGroupInfo.recorderPermission,
          vmax: { ...tempUserGroupInfo.recorderPermission.vmax, search: true },
        },
        userPermission: normalizeUserGroupUserPermission(
          tempUserGroupInfo.userPermission
        ),
        users: tempUserGroupInfo.users ?? [],
        accounts: tempUserGroupInfo.accounts ?? [],
        systems: filteringSystemByCameraSelected(
          tempUserGroupInfo.systems?.map((system: UserGroupCUSystem) =>
            normalizeSystemAllCameras({
              mergedSystemId: system.mergedSystemId,
              displayType: system.displayType,
              ...(checkTypeIsCovaOrVmax(system)
                ? {
                    allCameras: system.allCameras,
                    cameras: (system.cameras ?? []).map(
                      (camera: UserGroupCUSystemCamera) => ({
                        channel: camera.channel,
                        cameraId: camera.cameraId,
                        isAccess: camera.isAccess,
                        cameraUuid: camera.cameraUuid,
                      })
                    ),
                  }
                : { allCameras: true }),
            })
          )
        ),
        recorderGroups: tempUserGroupInfo.recorderGroups ?? [],
      });
      return;
    }
  }, [props.groupId, userGroupInfoResult, selectedAccount]);

  useEffect(() => {
    const getUniqueTypes = (
      items: Array<RecorderTotalListForUserGroupSystem>
    ) => [
      ...new Set(
        items.map((item) => {
          if (item.displayType.includes("VMAX")) {
            return "VMAX";
          }
          return item.displayType.toUpperCase();
        })
      ),
    ];

    const selectedSystemIdSet = new Set(
      (userGroup.systems ?? []).map((system) => system.mergedSystemId)
    );

    const filteredRecorders = recorderSystems.filter((rec) =>
      selectedSystemIdSet.has(rec.mergedSystemId)
    );

    // const filteredRecorderGroups = recorderGroups.filter((rec) =>
    //   userGroup.recorderGroups?.includes(rec.recorderGroupId)
    // );

    const tempSelectedTypes = getUniqueTypes([
      ...filteredRecorders,
      // ...filteredRecorderGroups,
    ]);

    const allRecorderTypes = getUniqueTypes([
      ...recorderSystems,
      // ...recorderGroups,
    ]);

    if (userGroup.autoApplyRecorder) {
      setSelectedRecorderTypes(allRecorderTypes);
    } else {
      setSelectedRecorderTypes(tempSelectedTypes);
    }
  }, [
    recorderSystems,
    recorderGroups,
    userGroup.autoApplyRecorder,
    userGroup.systems,
    userGroup.recorderGroups,
  ]);

  useEffect(() => {
    if (selectedAccount.accountLevel === LEVEL_TYPE.EU) {
      setRecorderRolePermissionDisabled(
        userGroup.userPermission.enableAllPermissions
      );
    }
  }, [
    userGroup.userPermission.enableAllPermissions,
    selectedAccount.accountLevel,
  ]);

  const onChangePage = (name: string, index?: number) => {
    if (page === 1 && checkDwPermissionIsFalse) {
      onValidateUserPermission();
      return;
    }

    if (
      page === 2 &&
      accountLevelExtendSubCp === LEVEL_TYPE.EU &&
      (checkNoRecorder || (isNoRecorder && !userGroup.autoApplyRecorder))
    ) {
      if (checkNoRecorder) {
        onValidateRecorder();
        return;
      }
    }

    if (name === "next") {
      setPage(page + 1);
    }
    if (name === "previous") {
      setPage(page - 1);
    }
    if (name === "save") {
      props.onModal();
    }
    if (name === "step" && index !== undefined) {
      setPage(index);
    }
  };

  const mutationCreateUserGroup = useStatusMutation(createGroup, {
    onSuccess: (res: any) => {
      if (res.error !== 0) {
        notify("error", intlString(lang.label.user.group.notify.add.fail));
      } else {
        notify("success", intlString(lang.label.user.group.notify.add.success));
        updateCurrentAccount();
        queryClient.invalidateQueries("group");
        props.onModal("edit");
      }
    },
    onError: (err: CustomError) => {
      handleErrorNotify(err, lang.label.user.group.notify.add.fail);
    },
  });

  const mutationUpdateUserGroup = useStatusMutation(updateGroup, {
    onSuccess: (res: any) => {
      if (res.error !== 0) {
        notify("error", intlString(lang.label.user.group.notify.update.fail));
      } else {
        notify(
          "success",
          intlString(lang.label.user.group.notify.update.success)
        );
        updateCurrentAccount();
        queryClient.invalidateQueries("group");
        props.onModal("edit");
      }
    },
    onError: (err: CustomError) => {
      handleErrorNotify(err, lang.label.user.group.notify.update.fail);
    },
  });

  const onClickCreate = useCallback(() => {
    if (checkDwPermissionIsFalse) {
      setPage(1);
      onValidateUserPermission();
      return;
    }
    if (
      accountLevelExtendSubCp === LEVEL_TYPE.EU &&
      ((checkNoRecorder && !isNoRecorder) ||
        (isNoRecorder && !userGroup.autoApplyRecorder))
    ) {
      if (checkNoRecorder) {
        onValidateRecorder();
        setPage(2);
        return;
      }
    }

    if (checkWhiteSpaceRegEx(userGroup.name)) {
      setUserGroupValidate((info) => {
        return { ...info, isUserGroupName: true as boolean };
      });

      setUserGroupValidateMsg((info) => {
        return {
          ...info,
          isUserGroupName: intlString(lang.validateMsg.user.group.name.empty),
        };
      });
      return;
    }

    if (isEditableUser && defaultGroupName.includes(userGroup.name)) {
      setUserGroupValidate((info) => {
        return { ...info, isUserGroupName: true as boolean };
      });

      setUserGroupValidateMsg((info) => {
        return {
          ...info,
          isUserGroupName: intlString(lang.validateMsg.userGroup.sameDefault),
        };
      });
      return;
    }

    if (userGroup.name === "administrator" && userGroup.users?.length === 0) {
      setUserGroupValidate((info) => {
        return { ...info, isUsers: true as boolean };
      });

      setUserGroupValidateMsg((info) => {
        return {
          ...info,
          isUsers: intlString(lang.validateMsg.userGroup.emptyAdmin),
        };
      });
      setPage(0);
      return;
    }

    // console.log(userGroup.userPermission);
    // return;

    if (!mutationCreateUserGroup.idle) return;
    if (!mutationUpdateUserGroup.idle) return;

    const payloadWithRecorderAllCameras = {
      ...userGroup,
      systems: applyAllCamerasByRecorderRole({
        systems: userGroup.systems,
        recorderPermission: userGroup.recorderPermission,
      }),
    };

    if (props.groupId) {
      mutationUpdateUserGroup.mutate({
        payload: payloadWithRecorderAllCameras,
        accountId: selectedAccount.accountId,
        groupId: props.groupId,
      });
    } else {
      mutationCreateUserGroup.mutate({
        payload: payloadWithRecorderAllCameras,
        accountId: selectedAccount.accountId,
      });
    }
  }, [
    userGroup,
    props.groupId,
    mutationCreateUserGroup,
    mutationUpdateUserGroup,
    selectedAccount.accountId,
    accountLevelExtendSubCp,
    checkNoRecorder,
    checkDwPermissionIsFalse,
    intlString,
    lang.validateMsg.user.group.name.empty,
    lang.validateMsg.userGroup.emptyAdmin,
    lang.validateMsg.userGroup.sameDefault,
    onValidateRecorder,
    onValidateUserPermission,
  ]);

  const mutationDeleteUserGroup = useStatusMutation(deleteGroup, {
    onSuccess: (res: any) => {
      if (res.error !== 0) {
        notify("error", intlString(lang.label.user.group.notify.delete.fail));
      } else {
        notify(
          "success",
          intlString(lang.label.user.group.notify.delete.success)
        );
        props.onModal("edit");
        props.onSuccessDelete && props.onSuccessDelete();
      }
    },
    onError: (err: CustomError) => {
      handleErrorNotify(err, lang.label.userGroup.notify.delete.fail);
    },
  });

  const onClickDelete = useCallback(() => {
    if (!mutationDeleteUserGroup.idle) return;
    if (!props.groupId) return;
    mutationDeleteUserGroup.mutate({
      accountId: selectedAccount.accountId,
      groupId: props.groupId,
    });
  }, [mutationDeleteUserGroup, selectedAccount.accountId, props.groupId]);

  const onChangeRecorderLevel = (value: number, type: RecorderType) => {
    setUserGroup((info) => {
      const nextRecorderPermission = {
        ...info.recorderPermission,
        [type]: {
          ...formatLevelCheckList({
            role: value as SpectrumRoleLevel | NVRRoleLevel,
            type,
            checkType: "check",
          }),
        },
      };

      return {
        ...info,
        recorderPermission: nextRecorderPermission,
        systems: applyAllCamerasByRecorderRole({
          systems: info.systems,
          recorderPermission: nextRecorderPermission,
        }),
      };
    });
    setRecorderPermissionDisableList((prev) => ({
      ...prev,
      [type]: {
        ...formatLevelCheckList({
          type,
          role: value as SpectrumRoleLevel | NVRRoleLevel,
          checkType: "disable",
        }),
      },
    }));
  };

  const onChangeDwPermission = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      let {
        target: { name, checked },
      } = e;
      const permissionTitle = e.target.getAttribute("data-title-name");

      // console.log(permissionTitle, name, checked);

      setUserGroupValidate((info) => {
        return { ...info, isUserPermission: false as boolean };
      });

      setUserGroupValidateMsg((info) => {
        return { ...info, isUserPermission: "" };
      });

      if (name === "enableAllPermissions") {
        const newPermissions = checked
          ? { ...defaultPermissions }
          : invertPermissionBooleans(defaultPermissions);

        const autoApplyFields = checkAutoApply({
          selectedAccount,
        });
        const isEuAdmin =
          isAdminGroup && accountLevelExtendSubCp === LEVEL_TYPE.EU;
        setUserGroup((info) => {
          const role = checked ? "admin" : "custom";

          const updatedInfo = {
            ...info,
            userPermission: newPermissions,
            ...autoApplyFields,
          };

          if (isEuAdmin) {
            return {
              ...updatedInfo,
              recorderPermission: {
                ...cloneDeep(allFormatLevel({ role, checkType: "check" })),
              },
            };
          }

          return updatedInfo;
        });

        const nvrRole = !checked ? 99 : 10;
        const spectrumRole = 0;

        if (isEuAdmin) {
          setRecorderPermissionDisableList({
            spectrum: {
              ...formatLevelCheckList({
                type: "spectrum",
                role: spectrumRole,
                checkType: "disable",
              }),
            } as SpectrumPermission,
            cova: {
              ...formatLevelCheckList({
                type: "cova",
                role: nvrRole,
                checkType: "disable",
              }),
            } as CovaPermission,
            vmax: {
              ...formatLevelCheckList({
                type: "vmax",
                role: nvrRole,
                checkType: "disable",
              }),
            } as VmaxPermission,
          });
        }
      }

      // console.log(permissionTitle, name);
      // manage 가 check === true 되면 view 도 자동으로 체크된다. (이 때 view 는 체크해제할 수 없음)

      if (permissionTitle === "user") {
        if (
          name === "viewUsersAndGroups" &&
          userGroup.userPermission.manageUsersAndGroups
        ) {
          return;
        }
        if (name === "manageUsersAndGroups") {
          setUserGroup((info) => {
            return {
              ...info,
              userPermission: {
                ...info.userPermission,
                viewUsersAndGroups: checked,
              },
            };
          });
        }
      }
      if (permissionTitle === "alert") {
        if (
          name === "viewAlerts" &&
          userGroup.userPermission.manageAlertRules
        ) {
          return;
        }
        if (name === "manageAlertRules") {
          setUserGroup((info) => {
            return {
              ...info,
              userPermission: {
                ...info.userPermission,
                viewAlerts: checked,
              },
            };
          });
        }
      }
      if (permissionTitle === "report") {
        if (
          name === "viewReports" &&
          userGroup.userPermission.manageReportRules
        ) {
          return;
        }
        if (name === "manageReportRules") {
          setUserGroup((info) => {
            return {
              ...info,
              userPermission: {
                ...info.userPermission,
                viewReports: checked,
              },
            };
          });
        }
      }
      if (permissionTitle === "recorders") {
        // if (
        //   name === "viewRecordersAndGroups" &&
        //   userGroup.userPermission.manageRecordersAndGroups
        // ) {
        //   return;
        // }
        // if (
        //   name === "manageRecordersAndGroups" &&
        //   !(
        //     userGroup.userPermission.viewAlerts ||
        //     userGroup.userPermission.viewReports
        //   )
        // ) {
        //   setUserGroup((info) => {
        //     return {
        //       ...info,
        //       userPermission: {
        //         ...info.userPermission,
        //         viewRecordersAndGroups: checked,
        //       },
        //     };
        //   });
        // }
      }
      if (permissionTitle === "dwBilling") {
        if (name === "subscriptionOrganization" && !checked) {
          setUserGroup((info) => {
            return {
              ...info,
              userPermission: {
                ...info.userPermission,
                suspend: false,
                lowLevelAccess: false,
              },
            };
          });
        }
        if ((name === "suspend" || name === "lowLevelAccess") && checked) {
          setUserGroup((info) => {
            return {
              ...info,
              userPermission: {
                ...info.userPermission,
                subscriptionOrganization: checked,
              },
            };
          });
        }

        if (name === "subscriptionKeys" && !checked) {
          setUserGroup((info) => {
            return {
              ...info,
              userPermission: {
                ...info.userPermission,
                generateKeys: false,
              },
            };
          });
        }
        if (name === "generateKeys" && checked) {
          setUserGroup((info) => {
            return {
              ...info,
              userPermission: {
                ...info.userPermission,
                subscriptionKeys: checked,
              },
            };
          });
        }

        if (name === "serviceAndPricing" && !checked) {
          setUserGroup((info) => {
            return {
              ...info,
              userPermission: {
                ...info.userPermission,
                serviceAndPricingUpdate: false,
              },
            };
          });
        }
        if (name === "serviceAndPricingUpdate" && checked) {
          setUserGroup((info) => {
            return {
              ...info,
              userPermission: {
                ...info.userPermission,
                serviceAndPricing: checked,
              },
            };
          });
        }

        if (name === "cumulus" && !checked) {
          setUserGroup((info) => {
            return {
              ...info,
              userPermission: {
                ...info.userPermission,
                generateCumulus: false,
              },
            };
          });
        }
        if (name === "generateCumulus" && checked) {
          setUserGroup((info) => {
            return {
              ...info,
              userPermission: {
                ...info.userPermission,
                cumulus: checked,
              },
            };
          });
        }
      }
      if (permissionTitle === "euSubscription") {
        if (
          name === "viewSubscription" &&
          userGroup.userPermission.manageSubscription
        ) {
          return;
        }
        if (name === "manageSubscription") {
          setUserGroup((info) => {
            return {
              ...info,
              userPermission: {
                ...info.userPermission,
                viewSubscription: checked,
              },
            };
          });
        }
      }

      setUserGroup((prev) => {
        const nextPermission = { ...prev.userPermission, [name]: checked };
        const onlyAccountLevelPermissions =
          isAllCheckForEnableAllPermission(nextPermission);
        const nextState = {
          ...prev,
          userPermission: {
            ...nextPermission,
            enableAllPermissions: onlyAccountLevelPermissions,
          },
        };
        if (
          selectedAccount.accountLevel === LEVEL_TYPE.EU &&
          onlyAccountLevelPermissions !==
            prev.userPermission.enableAllPermissions
        ) {
          return {
            ...nextState,
            recorderPermission: cloneDeep(
              allFormatLevel({
                role: onlyAccountLevelPermissions ? "admin" : "custom",
                checkType: "check",
              })
            ),
          };
        }
        return nextState;
      });
    },
    [userGroup.userPermission]
  );

  const onChangeRecorderPermission = useCallback(
    (type: RecorderType, e: ChangeEvent<HTMLInputElement>) => {
      let {
        target: { name, checked },
      } = e;

      if (type === "spectrum" && !checked) {
        if (name === "viewLive") {
          setUserGroup((info) => {
            return {
              ...info,
              recorderPermission: {
                ...info.recorderPermission,
                spectrum: {
                  ...info.recorderPermission.spectrum,
                  viewArchive: false,
                  cameraUserInput: false,
                  viewBookmark: false,
                  exportArchive: false,
                  modifyBookmark: false,
                  cameraSetting: false,
                },
              },
            };
          });
        }
        if (name === "viewArchive") {
          setUserGroup((info) => {
            return {
              ...info,
              recorderPermission: {
                ...info.recorderPermission,
                spectrum: {
                  ...info.recorderPermission.spectrum,
                  viewBookmark: false,
                  exportArchive: false,
                  modifyBookmark: false,
                },
              },
            };
          });
        }
        if (name === "viewBookmark") {
          setUserGroup((info) => {
            return {
              ...info,
              recorderPermission: {
                ...info.recorderPermission,
                spectrum: {
                  ...info.recorderPermission.spectrum,
                  modifyBookmark: false,
                },
              },
            };
          });
        }
        if (name === "cameraUserInput") {
          setUserGroup((info) => {
            return {
              ...info,
              recorderPermission: {
                ...info.recorderPermission,
                spectrum: {
                  ...info.recorderPermission.spectrum,
                  cameraSetting: false,
                },
              },
            };
          });
        }
      }

      setUserGroup((info) => {
        return {
          ...info,
          recorderPermission: {
            ...info.recorderPermission,
            [type]: {
              ...info.recorderPermission[type],
              [name]: checked,
            },
          },
        };
      });
    },
    [userGroup]
  );

  useEffect(() => {
    if (isEditableUser) {
      setRecorderPermissionDisableList({
        ...currentRecorderPermissionDisableList(userGroup.recorderPermission),
      });
    }
  }, [
    isEditableUser,
    userGroup.recorderPermission,
    setRecorderPermissionDisableList,
  ]);

  const onChangeGroupInfo = useCallback(
    (type: UPDATE_TYPE, value: any) => {
      if (type === UPDATE_TYPE.USERS) {
        setUserGroup((info) => {
          return { ...info, users: value as string[] };
        });
      } else if (type === UPDATE_TYPE.ACCOUNT) {
        setUserGroup((info) => {
          return { ...info, accounts: value as string[] };
        });
      } else if (type === UPDATE_TYPE.AUTO_APPLY) {
        if (value) {
          setUserGroupValidate((info) => {
            return { ...info, isRecorder: false as boolean };
          });

          setUserGroupValidateMsg((info) => {
            return {
              ...info,
              isRecorder: "",
            };
          });
          setUserGroup((info) => {
            return { ...info, systems: [], recorderGroups: [] };
          });
        }
        setUserGroup((info) => {
          return { ...info, autoApplyRecorder: value as boolean };
        });
      } else if (type === UPDATE_TYPE.AUTO_APPLY_ACCOUNT) {
        if (value) {
          setUserGroup((info) => {
            return { ...info, accounts: [] };
          });
        }
        setUserGroup((info) => {
          return { ...info, autoApplyAccount: value as boolean };
        });
      } else if (type === UPDATE_TYPE.RECORDERS) {
        if ((value as UserGroupCUSystem[]).length !== 0) {
          setUserGroupValidate((info) => {
            return { ...info, isRecorder: false };
          });
          setUserGroupValidateMsg((info) => {
            return { ...info, isRecorder: "" };
          });
        }
        setUserGroup((info) => {
          const nextSystems = filteringSystemByCameraSelected(
            (value as UserGroupCUSystem[]).map(normalizeSystemAllCameras)
          );

          return {
            ...info,
            systems: nextSystems,
            // recorderGroups: syncRecorderGroupsBySelectedSystems({
            //   recorderGroups: info.recorderGroups ?? [],
            //   previousSystems: info.systems,
            //   nextSystems,
            //   recorderGroupList: recorderGroupResult?.result,
            // }),
          };
        });
      } else if (type === UPDATE_TYPE.RECORDER_CAMEARS) {
        const nextSystem = value as UserGroupCUSystem;

        setUserGroupValidate((info) => {
          return { ...info, isRecorder: false };
        });
        setUserGroupValidateMsg((info) => {
          return { ...info, isRecorder: "" };
        });

        setUserGroup((info) => {
          const nextSystems = [...(info.systems ?? [])];
          const systemIndex = nextSystems.findIndex(
            (system) => system.mergedSystemId === nextSystem.mergedSystemId
          );

          if (systemIndex >= 0) {
            nextSystems[systemIndex] = normalizeSystemAllCameras({
              ...nextSystems[systemIndex],
              displayType: nextSystem.displayType,
              allCameras: areAllCamerasSelected(nextSystem.cameras),
              cameras: nextSystem.cameras as UserGroupCUSystemCamera[],
            });
          } else {
            nextSystems.push(normalizeSystemAllCameras(nextSystem));
          }

          const filteredSystems = filteringSystemByCameraSelected(nextSystems);

          return {
            ...info,
            systems: filteredSystems,
            // recorderGroups: syncRecorderGroupsBySelectedSystems({
            //   recorderGroups: info.recorderGroups ?? [],
            //   previousSystems: info.systems,
            //   nextSystems: filteredSystems,
            //   recorderGroupList: recorderGroupResult?.result,
            // }),
          };
        });
      } else if (type === UPDATE_TYPE.RECORDER_GROUPS) {
        if ((value as number[]).length !== 0) {
          setUserGroupValidate((info) => {
            return { ...info, isRecorder: false };
          });
          setUserGroupValidateMsg((info) => {
            return { ...info, isRecorder: "" };
          });
        }
        setUserGroup((info) => {
          const nextRecorderGroupIds = value as number[];
          const addedRecorderGroupIds = nextRecorderGroupIds.filter(
            (recorderGroupId) =>
              !(info.recorderGroups ?? []).includes(recorderGroupId)
          );

          const nextSystems = [...(info.systems ?? [])];

          addedRecorderGroupIds.forEach((recorderGroupId) => {
            const selectedGroup = recorderGroupResult?.result?.find(
              (group: RecorderGroupListResponse["result"][number]) =>
                group.recorderGroupId === recorderGroupId
            );

            selectedGroup?.systems?.forEach(
              (groupSystem: RecorderGroupSystem) => {
                const systemPayload = buildRecorderSystemPayload(
                  groupSystem.mergedSystemId
                );

                if (!systemPayload) {
                  return;
                }

                const existingSystemIndex = nextSystems.findIndex(
                  (system) =>
                    system.mergedSystemId === groupSystem.mergedSystemId
                );

                if (existingSystemIndex >= 0) {
                  nextSystems[existingSystemIndex] = {
                    ...nextSystems[existingSystemIndex],
                    ...systemPayload,
                  };
                } else {
                  nextSystems.push(systemPayload);
                }
              }
            );
          });

          return {
            ...info,
            recorderGroups: nextRecorderGroupIds,
            // systems: filteringSystemByCameraSelected(nextSystems),
          };
        });
      } else if (type === UPDATE_TYPE.NAME) {
        setUserGroup((info) => {
          return { ...info, name: value as string };
        });
      } else if (type === UPDATE_TYPE.DESC) {
        setUserGroup((info) => {
          return { ...info, description: value as string };
        });
      }
    },
    [buildRecorderSystemPayload, checkNoRecorder, recorderGroupResult?.result]
  );

  const onFocusPreferencePage = () => {
    setUserGroupValidate((info) => {
      return { ...info, isUserGroupName: false as boolean };
    });

    setUserGroupValidateMsg((info) => {
      return { ...info, isUserGroupName: "" };
    });
  };

  const isNoRecorder = useMemo(() => {
    return recorderGroups.length === 0 && recorderSystems.length === 0;
  }, [recorderGroups.length, recorderSystems.length]);

  const madeUserGroupUserId = useMemo(() => {
    if (
      props.groupId &&
      userGroupInfoResult?.result &&
      userGroupInfoResult.error === 0
    ) {
      return userGroupInfoResult.result.userId;
    }
    return auth.user.userId;
  }, [userGroupInfoResult, props.groupId]);

  return (
    <AddUserGroupPresenter
      {...props}
      page={page}
      onChangeGroupInfo={onChangeGroupInfo}
      userGroupValidate={userGroupValidate}
      userGroupValidateMsg={userGroupValidateMsg}
      userGroup={userGroup}
      isEditableUser={isEditableUser}
      isNoRecorder={isNoRecorder}
      onFocusPreferencePage={onFocusPreferencePage}
      onChangeDwPermission={onChangeDwPermission}
      onChangeRecorderLevel={onChangeRecorderLevel}
      onClickDelete={onClickDelete}
      onClickCreate={onClickCreate}
      recorderRolepermissionDisabled={recorderRolepermissionDisabled}
      onChangePage={onChangePage}
      onChangeRecorderPermission={onChangeRecorderPermission}
      myDwDisabledList={myDwDisabledList}
      selectedRecorderTypes={selectedRecorderTypes}
      recorderPermissionDisabledList={recorderPermissionDisabledList}
      deleteDisabled={!mutationDeleteUserGroup.idle}
      saveDisabled={
        !mutationCreateUserGroup.idle || !mutationUpdateUserGroup.idle
      }
      madeUserGroupUserId={madeUserGroupUserId}
    />
  );
}

export default AddUserGroupContainer;
